import { cn } from '../../lib/utils';

const PALETTE_SIZE = 6;

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0; // keep it a 32-bit int
  }
  return Math.abs(hash);
}

function initialsOf(name: string): string {
  const initials = name
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  return initials || '?';
}

interface AvatarProps {
  /** Display name or email — used both for the initials and the colour hash,
   * so the same person always gets the same colour everywhere they appear. */
  name: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Deterministic coloured initials chip. Colour comes from a fixed 6-hue
 * palette defined in styles.css (`.avatar-0`..`.avatar-5`, themed light/dark
 * the same way every other colour token in the app is) rather than computed
 * at runtime — keeps contrast correct in both themes without per-instance
 * inline-style math. Purely decorative identity, not a status signal, so it
 * deliberately doesn't reuse Badge's tone palette.
 */
export function Avatar({ name, size = 'md', className }: AvatarProps) {
  const hue = hashString(name) % PALETTE_SIZE;
  return (
    <span
      aria-hidden
      className={cn(
        `avatar-${hue}`,
        'grid shrink-0 place-items-center rounded-full font-mono font-medium',
        size === 'sm' ? 'size-6 text-[0.625rem]' : 'size-8 text-xs',
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}
